import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { strategies } from '../data/registry';

/**
 * Strategies published in more than one version (same signal, different vehicles), e.g. HEIMDALL
 * Standard | UCITS. A registry entry opts in with `variants: { <key>: curve, ... }` and
 * `defaultVariant`. The chosen version is kept per strategy and remembered per browser; every page
 * that reads curves through the sized hooks (IntradaySizingContext) gets the chosen one.
 */

export const STRATEGY_VARIANTS = Object.fromEntries(
  strategies.filter((s) => s.variants).map((s) => [s.slug, { keys: Object.keys(s.variants), def: s.defaultVariant || Object.keys(s.variants)[0], curves: s.variants }]),
);
export const VARIANT_SLUGS = Object.keys(STRATEGY_VARIANTS);
const STORAGE_KEY = 'hma.strategyVariant.v1';

const DEFAULTS = Object.fromEntries(VARIANT_SLUGS.map((slug) => [slug, STRATEGY_VARIANTS[slug].def]));

function load() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}') || {};
    const out = { ...DEFAULTS };
    for (const slug of VARIANT_SLUGS) {
      if (STRATEGY_VARIANTS[slug].keys.includes(parsed[slug])) out[slug] = parsed[slug];
    }
    return out;
  } catch {
    return DEFAULTS;
  }
}

/** Curve of `slug` for the chosen version (or `fallback` when the strategy has no versions). */
export function variantCurve(slug, chosen, fallback) {
  const v = STRATEGY_VARIANTS[slug];
  if (!v) return fallback;
  return v.curves[chosen] || v.curves[v.def] || fallback;
}

const Ctx = createContext({ all: DEFAULTS, setFor: () => {} });

export function StrategyVariantProvider({ children }) {
  const [all, setAll] = useState(load);
  const setFor = useCallback((slug, key) => {
    if (!STRATEGY_VARIANTS[slug]?.keys.includes(key)) return;
    setAll((prev) => {
      const next = { ...prev, [slug]: key };
      try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  }, []);
  const value = useMemo(() => ({ all, setFor }), [all, setFor]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Chosen version of ONE strategy. */
export function useStrategyVariant(slug) {
  const { all, setFor } = useContext(Ctx);
  const variant = all[slug] || STRATEGY_VARIANTS[slug]?.def || null;
  const setVariant = useCallback((key) => setFor(slug, key), [setFor, slug]);
  return { variant, setVariant, keys: STRATEGY_VARIANTS[slug]?.keys || [] };
}

/** All chosen versions, keyed by slug. */
export function useAllStrategyVariants() {
  return useContext(Ctx).all;
}
